import { ProjectRunConfigurationStatus } from "@app/projects/enumerators";
import ProjectService from "@app/projects/projectService";
import moment from "moment";
import { FC, JSX, useContext, useEffect, useState } from "react";
import { toast, TypeOptions } from "react-toastify";
import { ResourceType } from "@app/resources";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { BulkPublishModel } from "@app/projects/bulkPublishModel";
import { BulkProjectModel } from "@app/projects/bulkProjectModel";
import ZipProjectService from "@app/projects/zipProjectService";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { sortByDate } from "@app/shared/sortByDate";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import ResourceService from "@app/resources/resourceService";

interface BulkProjectActionProps {
  onArchive?: (archivedIds: number[]) => void;
  onClose?: () => void; // optionally passed from SidePanel
  onPublish: () => void;
  onSubmit?: () => void;
  resourceIds: number[];
  setProjectIsReadOnly: (projectIds: number[]) => void;
  showArchive: boolean;
  showPublish: boolean;
  loadingResourceIds: boolean;
}

interface BulkActionModel {
  /* oxlint-disable sort-keys */
  [wafflesKeyColumnName]: string;
  name: string;
  projectId: number;
  resourceId: number;
  lastRunDate?: string;
  lastRunStatus?: ProjectRunConfigurationStatus;
  previousPublishDate?: string;
  resourceType: ResourceType;
  /* oxlint-enable sort-keys */
}

enum BulkActions {
  Select = "Select an Action",
  Run = "Run Projects",
  Publish = "Publish Projects",
  Archive = "Archive Projects",
}

const projectService = new ProjectService();
const zipProjectService = new ZipProjectService();

const BulkProjectActions: FC<BulkProjectActionProps> = (props: BulkProjectActionProps): JSX.Element => {
  const [action, setAction] = useState<BulkActions>(BulkActions.Select);
  const [comment, setComment] = useState("");
  const [data, setData] = useState<BulkActionModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<BulkActionModel[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [archiveDoubleCheck, setArchiveDoubleCheck] = useState(false);
  const { setIsFlyoutVisible } = useContext(FlyoutContext);

  let actions: BulkActions[] = [BulkActions.Run];

  if (props.showPublish)
    actions.push(BulkActions.Publish);

  if (props.showArchive)
    actions.push(BulkActions.Archive);

  actions = actions.sort();
  actions.unshift(BulkActions.Select);

  const sortData = (data: (BulkProjectModel & BulkPublishModel)[]): BulkActionModel[] => data.sort((a, b) => a.name > b.name ? 1 : -1)
    .map(rec => {
      /* oxlint-disable sort-keys */
      return ({
        key: rec.projectId.toString(),
        name: rec.name,
        projectId: rec.projectId,
        resourceId: rec.resourceId,
        resourceType: rec.resourceType,
        lastRunDate: !!rec.lastRunDate ? moment.utc(rec.lastRunDate).format("MMMM D, YYYY h:mm A") : "---",
        lastRunStatus: !!rec.lastRunStatus ? getPrintableRunStatus(rec.lastRunStatus) : "---",
        previousPublishDate: !!rec.previousPublishDate ? moment.utc(rec.previousPublishDate).format("MMMM D, YYYY h:mm A") : "---",
      }) as BulkActionModel;
      /* oxlint-enable sort-keys */
    })

  useEffect(() => {
    if (!props.loadingResourceIds && !submitting) {
      loadProjects();
    }
  }, [action, props.resourceIds]);

  const loadProjects = () => {
    switch (action) {
      case BulkActions.Run:
        projectService.getRunnableProjects((props.resourceIds)).then((newData) => {
          // make sure we don't keep any selections that are no longer available
          const newSelected = selected.filter((s) => newData.find((d) => d.projectId === s.projectId));
          // React should batch all these updates together
          setData(sortData(newData));
          setLoading(false);
          setSelected(newSelected);
          setSubmitting(false);
        });
        break;
      case BulkActions.Publish:
        projectService.getPublishableProjects((props.resourceIds)).then((newData) => {
          const newSelected = selected.filter((s) => newData.find((d) => d.projectId === s.projectId));
          setData(sortData(newData));
          setLoading(false);
          setSelected(newSelected);
          setSubmitting(false);
        });
        break;
      case BulkActions.Archive:
        projectService.getArchivableProjects((props.resourceIds)).then((newData) => {
          // make sure we don't keep any selections that are no longer available
          const newSelected = selected.filter((s) => newData.find((d) => d.projectId === s.projectId));
          setData(sortData(newData));
          setLoading(false);
          setSelected(newSelected);
          setSubmitting(false);
        });
        break;
      case "Select an Action":
        setData([]);
        setLoading(false);
        setSelected([]);
        setSubmitting(false);
    }
  }

  const handleSubmit = async () => {
    toast.dismiss();
    const response: number[] = [];
    setSubmitting(true);
    const countyProjects: number[] = [];
    const zipProjects: number[] = [];
    switch (action) {
      case BulkActions.Run:
        selected.forEach((p) => {
          if (p.resourceType === ResourceType.ZipAnalysisProject) {
            zipProjects.push(p.projectId);
          } else {
            countyProjects.push(p.projectId);
          }
        });
        props.setProjectIsReadOnly(countyProjects.concat(zipProjects));
        const promises = [
          projectService.bulkRun(countyProjects),
          zipProjectService.bulkRunZipProjects(zipProjects),
        ];
        const runResponses = await Promise.all(promises);
        // the county project bulk run response
        runResponses.forEach((rr) => {
          if (rr) {
            response.push(...rr.submitResults.map((sr) => sr.projectId));
          }
        })
        props.onSubmit();
        break;
      case BulkActions.Publish:
        selected.forEach((p) => {
          if (p.resourceType === ResourceType.ZipAnalysisProject) {
            zipProjects.push(p.projectId);
          } else {
            countyProjects.push(p.projectId);
          }
        });
        try {
          const promises = [
            projectService.bulkPublish(countyProjects, comment),
            zipProjectService.bulkPublishZipProjects(zipProjects, comment),
          ]
          const publishResponses = await Promise.all(promises);
          publishResponses.forEach((pr) => {
            if (pr) {
              response.push(...pr);
            }
          })
          props.onPublish();
        }
        catch (e) {
          let newStatus = "";
          let toastType: TypeOptions = "error";
          switch (e.status) {
            case 409:
              newStatus = "One of the selected projects is in a read only state, no projects published."
              toastType = "warning";
              break;
            default:
              newStatus = "There was an error publishing the selected projects.";
              toastType = "error";
          }
          handleExceptionToast(newStatus, toastType);
          setLoading(true);
          loadProjects();
          return;
        }

        break;
      case BulkActions.Archive:
        setArchiveDoubleCheck(true);
        return;
    }
    handleToast(response);
  };

  const handleToast = (response: unknown[]) => {
    let newStatus = "";
    let toastType: TypeOptions = "default";
    if (response.length === 0) {
      newStatus = "The request could not be completed.";
      toastType = "error";
    } else if (response.length < selected.length) {
      newStatus = "Only some of the projects were updated successfully.";
      toastType = "warning";
    } else if (response.length === selected.length) {
      newStatus = "Operation complete!";
      toastType = "success";
    }
    if (!setIsFlyoutVisible) {
      setSubmitting(false);
      toast(newStatus, {
        autoClose: false,
        containerId: "z-index-9999",
        type: toastType,
      });
    } else {
      toast(newStatus, {
        containerId: "z-index-9999",
        type: toastType,
      });
      setIsFlyoutVisible(false);
    }
  }

  const handleExceptionToast = (newStatus: string, toastType: TypeOptions = "warning") => {
    setSubmitting(false);
    toast(newStatus, {
      autoClose: false,
      containerId: "z-index-9999",
      type: toastType,
    });
  }

  const actuallyBulkArchive = () => {
    const resourceService = new ResourceService();
    resourceService.bulkArchive(selected.map((project) => project.resourceId)).then((response) => {
      if (!setIsFlyoutVisible) {
        setArchiveDoubleCheck(false);
        setSubmitting(false);
      }
      props.onArchive(response);
      handleToast(response);
    }, () => {
      handleExceptionToast("We encountered an error. Please refresh and try again.", "error");
    });
  }

  const renderMainView = () => {
    return (
      <>
        <QESSelect
          label="Action"
          containerClassName="mb-3 col-4"
          disabled={(loading || archiveDoubleCheck) && !!action}
          id="action"
          value={action}
          onChange={(e) => { setAction(e.target.value as BulkActions); setLoading(true); }}
          testId="bulk-project-actions__select"
        >
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </QESSelect>
        {action === BulkActions.Publish && (
          <QESTextArea
            label="Comment:"
            testId="bulk-actions__publish-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="col-4"
            containerClassName="mb-3"
          />
        )}
        {action === BulkActions.Archive && (
          <QESAlert type="warning" persist={true}>Selected projects will be archived.</QESAlert>
        )}

        {loading ? <LoadingSpinner /> : renderTable()}

        <FlyoutFooter
          submitButton={
            <QESButton
              qesTestId="bulk-project-actions_submit-changes"
              className="offset-2 mt-3"
              disabled={selected.length === 0 || loading || submitting}
              onClick={handleSubmit}
            >
              {(submitting ? "Submitting Request..." : "Submit Changes")}
            </QESButton>
          }
        />
      </>
    );
  }

  const columnConfig: ColumnConfig = {
    lastRunDate: {
      displayText: "Last Run",
      hidden: action !== BulkActions.Run,
      testId: "bulk-actions__last-run-date",
    },
    lastRunStatus: {
      displayText: "Status of Last Run",
      hidden: action !== BulkActions.Run,
      testId: "bulk-actions__last-run-status",
    },
    name: {
      displayText: "Project",
      hidden: false,
      testId: "bulk-actions__project-name",
    },
    previousPublishDate: {
      displayText: "Last Publish",
      hidden: action !== BulkActions.Publish,
      noResize: true,
      testId: "bulk-actions__previous-publish-date",
    },
  };

  const handleWaffleSelectionChange = (selectedProjectIds: string[]) => {
    const selectedProps = data.filter((sr) => {
      return selectedProjectIds.includes(sr.projectId.toString());
    });
    setSelected(selectedProps);
  }

  const handleSort = (records: BulkActionModel[], column: keyof BulkActionModel, direction: SortDirections) => {
    const data = [...records];
    if (direction === SortDirections.None) {
      return data;
    }
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (column) {
      case "name":
      case "lastRunStatus":
        data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
        break;
      case "lastRunDate":
        return sortByDate(data, "lastRunDate", direction, "key")
      case "previousPublishDate":
        return sortByDate(data, "previousPublishDate", direction, "key")
    }
    return data;
  }

  const selectedKeys = [] = selected.map(d => d.projectId.toString())
  const renderTable = () => {
    if (!!data.length) {
      return (
        <Waffles<BulkActionModel>
          columns={columnConfig}
          customSort={handleSort}
          data={data}
          fullWidth={true}
          handleCheckboxChange={handleWaffleSelectionChange}
          selectedKeys={selectedKeys}
          showCheckboxes={true}
          testId="bulk-actions-table"
        />
      );
    } else if (action !== "Select an Action") {
      return <p>None of your projects are ready to {action.toLowerCase()}.</p>;
    }
  };

  const renderArchiveDoubleCheck = () => {
    return (
      <>
        <p>Are you sure you want to archive the selected project(s)?</p>
        <ul>
          {selected.map((project) => <li key={project.projectId}>{project.name}</li>)}
        </ul>
        <FlyoutFooter
          submitButton={
            <div>
              <QESButton qesType="secondary-danger" qesTestId="bulk-project-actions__cancel-archive" className="me-3" onClick={() => { setArchiveDoubleCheck(false); setSubmitting(false); }}>Previous</QESButton>
              <QESButton qesType="primary-danger" qesTestId="bulk-project-actions__confirm-archive" onClick={actuallyBulkArchive}>Archive</QESButton>
            </div>
          }
        />
      </>
    );
  }

  return (
    <>
      {archiveDoubleCheck ? renderArchiveDoubleCheck() : renderMainView()}
    </>
  );
};

function getPrintableRunStatus(status: ProjectRunConfigurationStatus) {
  switch (status) {
    case ProjectRunConfigurationStatus.QueuedProviderChanges:
    case ProjectRunConfigurationStatus.ProcessingProviderChanges:
      return "Processing Provider Changes";
    case ProjectRunConfigurationStatus.QueuedSpecialtyGroupExceptionAnalysis:
    case ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis:
      return "Recalculating results for Specialty Group Exception Change";
    case ProjectRunConfigurationStatus.QueuedProspectMarketNcp:
    case ProjectRunConfigurationStatus.ProcessingProspectMarketNcp:
      return "Processing Next Closest Providers";
    default:
      return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
  }
}

export default BulkProjectActions;